import { Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE } from '../components/motion.tsx';
import { PlacementFields } from '../components/PersonEdit.tsx';
import { SetupLinkDialog } from '../components/GxMail.tsx';
import { Avatar, Button, Callout, Dialog, Empty, Field, PageHead, Segmented, Skeleton, StatusChip, Switch, useToast } from '../components/ui.tsx';
import { ApiError, get, photoUrl, post, qs, upload } from '../lib/api.ts';
import { DOCUMENT_HINT, DOCUMENT_TYPES, documentProblem, PERSON_DOCUMENTS, type PersonDocumentKind } from '../lib/documents.ts';
import { date, todayIn } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface PersonRow {
  id: string;
  name: string;
  photoFileId: string | null;
  employeeNo: string | null;
  status: string;
  jobPosition: string | null;
  division: string | null;
  officeName: string | null;
  type: string | null;
  contractEndsOn: string | null;
  hiredOn: string | null;
}

export function People() {
  const me = useMe().data!;
  const lookups = useLookups();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [status, setStatus] = useState<'active' | 'onboarding' | 'resigned' | 'all'>('active');
  const [view, setView] = useState<'cards' | 'list'>(() => (localStorage.getItem('hearth.people.view') as 'cards' | 'list') ?? 'cards');
  const [recruit, setRecruit] = useState(false);
  // Selecting onboarding records to cancel together (old test registrations)
  const canBulkCancel = status === 'onboarding' && me.access.recruit;
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCancel, setBulkCancel] = useState(false);
  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  useEffect(stopSelecting, [status]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 220);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    try {
      localStorage.setItem('hearth.people.view', view);
    } catch {
      /* ignore */
    }
  }, [view]);

  const list = useQuery({
    queryKey: ['people', debounced, officeId, status],
    queryFn: () => get<{ total: number; items: PersonRow[] }>(`/api/people${qs({ q: debounced, officeId, status, size: 120 })}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <PageHead
        eyebrow={`People · ${list.data?.total ?? '…'} ${status === 'all' ? 'in total' : status}`}
        title={
          <>
            Everyone, <em>in one place.</em>
          </>
        }
        lede={me.access.peopleAll ? 'Search by name, employee number or role. Sensitive details stay masked until you have a reason to see them.' : 'The people in the teams you manage.'}
        aside={
          <>
            <Button icon="download" title="The list as shown, with every column of the old employee export" onClick={() => window.location.assign(`/api/people/export.xlsx${qs({ q: debounced.trim(), officeId, status })}`)}>
              Excel
            </Button>
            {me.access.recruit ? (
              <Button variant="primary" icon="plus" onClick={() => setRecruit(true)}>
                Recruit
              </Button>
            ) : null}
          </>
        }
      />
      <div className="toolbar">
        <div className="input-group">
          <Icon name="search" />
          <input className="input" placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
        </div>
        <select className="select" style={{ width: 'auto' }} value={officeId} onChange={(e) => setOfficeId(e.target.value)} aria-label="Office">
          <option value="">All offices</option>
          {lookups.data?.offices.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <Segmented
          label="Status"
          idPrefix="status"
          value={status}
          onChange={setStatus}
          options={[
            { id: 'active', label: 'Active' },
            { id: 'onboarding', label: 'Onboarding' },
            { id: 'resigned', label: 'Resigned' },
            { id: 'all', label: 'All' },
          ]}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Segmented label="View" idPrefix="view" value={view} onChange={setView} options={[{ id: 'cards', label: 'Cards' }, { id: 'list', label: 'List' }]} />
        </div>
      </div>
      {canBulkCancel && list.data?.items.length ? (
        <div className="row row--wrap" style={{ gap: 8, margin: '0 0 var(--s-4)' }}>
          {selecting ? (
            <>
              <span className="muted" style={{ fontSize: 'var(--step--1)', marginRight: 4 }}>{selected.size} selected</span>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(list.data!.items.map((p) => p.id)))}>
                Select all {list.data.items.length}
              </Button>
              <Button size="sm" variant="ghost" disabled={!selected.size} onClick={() => setSelected(new Set())}>
                Clear
              </Button>
              <Button size="sm" variant="danger" icon="x" disabled={!selected.size} onClick={() => setBulkCancel(true)}>
                Cancel {selected.size || ''} onboarding{selected.size === 1 ? '' : 's'}
              </Button>
              <Button size="sm" variant="ghost" onClick={stopSelecting}>
                Done
              </Button>
            </>
          ) : (
            <Button size="sm" icon="check" onClick={() => setSelecting(true)}>
              Select to cancel
            </Button>
          )}
        </div>
      ) : null}

      {list.isLoading ? (
        <div className="people-grid">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="person-card">
              <Skeleton w={44} h={44} r={22} />
              <Skeleton h={20} w="70%" />
              <Skeleton h={12} w="50%" />
            </div>
          ))}
        </div>
      ) : !list.data?.items.length ? (
        <Empty title="No one matches.">Try a different spelling, or clear the office filter.</Empty>
      ) : view === 'cards' ? (
        <motion.div className="people-grid" layout initial="hidden" animate="shown" variants={{ shown: { transition: { staggerChildren: 0.018 } } }}>
          <AnimatePresence mode="popLayout">
            {list.data.items.map((p) => (
              <motion.div key={p.id} layout variants={{ hidden: { opacity: 0, y: 10 }, shown: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } }} exit={{ opacity: 0, scale: 0.97 }}>
                <Link
                  to="/people/$id"
                  params={{ id: p.id }}
                  className={`person-card${selecting && selected.has(p.id) ? ' person-card--selected' : ''}`}
                  onClick={(e) => {
                    if (!selecting) return;
                    e.preventDefault();
                    toggle(p.id);
                  }}
                >
                  <div className="row row--between" style={{ alignItems: 'start' }}>
                    <Avatar name={p.name} size={46} photo={p.photoFileId ? photoUrl(p.id, { version: p.photoFileId }) : null} />
                    {selecting ? <input type="checkbox" className="pick" checked={selected.has(p.id)} readOnly tabIndex={-1} aria-label={`Select ${p.name}`} /> : p.status !== 'active' ? <StatusChip status={p.status} /> : p.contractEndsOn && p.contractEndsOn <= addMonths(todayIn(), 1) ? <span className="chip chip--wait">Contract ends {date(p.contractEndsOn, { year: false })}</span> : null}
                  </div>
                  <div>
                    <div className="person-card__name">{p.name}</div>
                    <div className="muted" style={{ fontSize: 'var(--step--1)', marginTop: 4 }}>
                      {p.jobPosition ?? '—'}
                    </div>
                  </div>
                  <div className="person-card__meta">
                    <span>{p.division ?? p.officeName}</span>
                    <span className="mono">{p.employeeNo}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <PeopleTable rows={list.data.items} pick={selecting ? { selected, toggle } : undefined} />
      )}
      <RecruitDialog open={recruit} onClose={() => setRecruit(false)} />
      {bulkCancel && list.data ? (
        <BulkCancelDialog
          people={list.data.items.filter((p) => selected.has(p.id))}
          onClose={() => setBulkCancel(false)}
          onDone={(cancelledIds) => setSelected((s) => new Set([...s].filter((id) => !cancelledIds.includes(id))))}
        />
      ) : null}
    </div>
  );
}

function addMonths(iso: string, n: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

function PeopleTable({ rows, pick }: { rows: PersonRow[]; pick?: { selected: Set<string>; toggle: (id: string) => void } }) {
  const navigate = useNavigate();
  return (
    <div className="panel" style={{ padding: '6px 24px' }}>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {pick ? <th style={{ width: 32 }} /> : null}
              <th>Name</th>
              <th>No.</th>
              <th>Position</th>
              <th>Division</th>
              <th>Office</th>
              <th>Type</th>
              <th>Since</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-href onClick={() => (pick ? pick.toggle(p.id) : navigate({ to: '/people/$id', params: { id: p.id } }))}>
                {pick ? (
                  <td>
                    <input type="checkbox" className="pick" checked={pick.selected.has(p.id)} onChange={() => pick.toggle(p.id)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${p.name}`} />
                  </td>
                ) : null}
                <td>
                  <div className="row">
                    <Avatar name={p.name} size={28} photo={p.photoFileId ? photoUrl(p.id, { version: p.photoFileId }) : null} />
                    <Link to="/people/$id" params={{ id: p.id }} className="link" onClick={(e) => e.stopPropagation()}>
                      {p.name}
                    </Link>
                  </div>
                </td>
                <td className="mono faint">{p.employeeNo}</td>
                <td>{p.jobPosition}</td>
                <td className="muted">{p.division}</td>
                <td className="muted">{p.officeName}</td>
                <td className="muted" style={{ textTransform: 'capitalize' }}>
                  {p.type}
                </td>
                <td className="mono faint">{date(p.hiredOn)}</td>
                <td>
                  <StatusChip status={p.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Cancels the selected onboardings with one reason; anyone the API refuses (they have started) is listed. */
function BulkCancelDialog({ people, onClose, onDone }: { people: PersonRow[]; onClose: () => void; onDone: (cancelledIds: string[]) => void }) {
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => post<{ cancelled: number; onMachines: number; refused: { id: string; name: string | null; reason: string }[] }>('/api/people/cancel-onboarding', { ids: people.map((p) => p.id), reason }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      onDone(people.map((p) => p.id).filter((id) => !r.refused.some((x) => x.id === id)));
      if (r.cancelled) toast({ title: `${r.cancelled} onboarding${r.cancelled === 1 ? '' : 's'} cancelled`, body: r.onMachines ? `${r.onMachines} attendance-machine entr${r.onMachines === 1 ? 'y remains' : 'ies remain'}: remove them under Devices.` : undefined });
      if (!r.refused.length) onClose();
    },
  });
  const refused = m.data?.refused ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Onboarding"
      title={<>Cancel {people.length} onboarding{people.length === 1 ? '' : 's'}?</>}
      width={560}
      footer={
        refused.length ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Keep
            </Button>
            <Button variant="danger" icon="x" loading={m.isPending} disabled={reason.trim().length < 3} onClick={() => m.mutate()}>
              Cancel {people.length}
            </Button>
          </>
        )
      }
    >
      <div className="stack">
        {refused.length ? (
          <Callout tone="warn" title={`${m.data!.cancelled} cancelled; ${refused.length} not`}>
            <ul className="list-plain">
              {refused.map((x) => (
                <li key={x.id} style={{ marginTop: 6 }}>
                  <strong>{x.name ?? x.id}</strong>: {x.reason}
                </li>
              ))}
            </ul>
          </Callout>
        ) : (
          <>
            <p className="muted">Each record leaves the directory but is kept for the audit trail; their accounts and set-up links stop working. Anyone who already has attendance or a payslip is skipped (record a resignation for them).</p>
            <ul className="list-plain bulk-names">
              {people.map((p) => (
                <li key={p.id} className="mini-row">
                  <span>{p.name}</span>
                  <span className="mini-row__aside mono">{p.employeeNo ?? '—'} · since {date(p.hiredOn)}</span>
                </li>
              ))}
            </ul>
            <Field label="Reason" hint="One reason for all of them, kept in the audit trail, e.g. “old test registrations”.">
              {(a) => <textarea {...a} className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />}
            </Field>
            {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Something went wrong.'}</Callout> : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

const PTKP = ['TK/0', 'TK/1', 'TK/2', 'TK/3', 'K/0', 'K/1', 'K/2', 'K/3'];
const BLANK = { givenName: '', familyName: '', preferredName: '', gender: 'female', birthDate: '', birthPlace: '', religionId: '', maritalStatusId: '', educationLevelId: '', workEmail: '', personalEmail: '', mobilePhone: '', companyId: '', officeId: '', departmentId: '', divisionId: '', sectionId: '', jobPositionId: '', jobLevelId: '', type: 'contract', startedOn: todayIn(), contractEndsOn: '', basicSalary: '', nationalId: '', familyCard: '', npwp: '', bpjsEmployment: '', bpjsHealth: '', ptkpCode: 'TK/0', bankId: '', accountNo: '', holderName: '', branch: '' };

function RecruitDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const lookups = useLookups();
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState(BLANK);
  const [gxMail, setGxMail] = useState(false);
  const [docs, setDocs] = useState<Partial<Record<PersonDocumentKind, File>>>({});
  // A hire whose sign-in is their new GX Mail address and who has no private e-mail: HR hands the set-up link over.
  const [handOver, setHandOver] = useState<{ id: string; link: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  // Scans are restricted: only HR who may edit records and see sensitive ones uploads them (the API checks the same).
  const canUpload = me.access.employeeEdit && me.access.sensitive;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => {
    if (lookups.data && !f.companyId) setF((x) => ({ ...x, companyId: lookups.data!.companies.find((c) => c.isPrimary)?.id ?? '', officeId: lookups.data!.offices[0]?.id ?? '', bankId: lookups.data!.banks[0]?.id ?? '' }));
  }, [lookups.data, f.companyId]);
  const done = (id: string) => {
    onClose();
    setF(BLANK);
    setDocs({});
    navigate({ to: '/people/$id', params: { id } });
  };
  const docProblem = Object.values(docs).map((file) => documentProblem(file ?? null)).find(Boolean) ?? null;
  const m = useMutation({
    mutationFn: async () => {
      const { bankId, accountNo, holderName, branch, ...rest } = f;
      const fullName = `${f.givenName} ${f.familyName}`.trim();
      const bank = accountNo ? { bankId, accountNo, holderName: holderName || fullName, branch: branch || null } : undefined;
      const r = await post<{ id: string; employeeNo: string | null; setupLink: string | null; gxMail: { address: string } | { error: string } | null }>('/api/people', { ...Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== '')), gxMail, bank });
      // The hire exists now: a failed scan is reported and uploaded again from the profile.
      const failed: string[] = [];
      for (const d of PERSON_DOCUMENTS) {
        const file = docs[d.kind];
        if (!file || (d.kind === 'bank_book' && !bank)) continue;
        await upload(`/api/people/${r.id}/documents/${d.kind}`, file).catch(() => failed.push(d.label));
      }
      return { ...r, failed };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['people'] });
      toast({ title: `${f.givenName} added`, body: r.employeeNo ? `Employee number ${r.employeeNo}. They’ll set a password on first sign-in.` : 'They’ll set a password on first sign-in.' });
      if (r.gxMail && 'error' in r.gxMail) toast({ title: `No GX Mail address yet: ${r.gxMail.error}`, body: 'Create it from their profile.', tone: 'error' });
      if (r.failed.length) toast({ title: `Not uploaded: ${r.failed.join(', ')}`, body: 'Upload them again from their profile, under Documents.', tone: 'error' });
      if (r.setupLink) setHandOver({ id: r.id, link: r.setupLink });
      else done(r.id);
    },
    onError: (e) => setErrors(e instanceof ApiError && e.details && typeof e.details === 'object' ? (e.details as Record<string, string[]>) : { _: [e instanceof Error ? e.message : 'Failed'] }),
  });
  const err = (k: string) => errors[k]?.[0] ?? null;
  const input = (label: string, k: keyof typeof f, cols: number, opts: { type?: string; mono?: boolean; hint?: string; inputMode?: 'numeric' | 'email' | 'tel'; placeholder?: string; maxLength?: number; digits?: boolean } = {}) => (
    <div className={`col-${cols}`}>
      <Field label={label} hint={opts.hint} error={err(k)}>
        {(p) => <input {...p} className={`input${opts.mono ? ' input--mono' : ''}`} type={opts.type ?? 'text'} inputMode={opts.inputMode} placeholder={opts.placeholder} maxLength={opts.maxLength} value={f[k]} onChange={opts.digits ? (e) => setF((x) => ({ ...x, [k]: e.target.value.replace(/\D/g, '') })) : set(k)} />}
      </Field>
    </div>
  );
  const select = (label: string, k: keyof typeof f, cols: number, options: { id: string; name: string }[] | undefined, opts: { hint?: string; blank?: boolean } = {}) => (
    <div className={`col-${cols}`}>
      <Field label={label} hint={opts.hint} error={err(k)}>
        {(p) => (
          <select {...p} className="select" value={f[k]} onChange={set(k)}>
            {opts.blank === false ? null : <option value="">—</option>}
            {options?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </Field>
    </div>
  );
  const heading = (title: string, note?: string) => (
    <div className="col-12" style={{ marginTop: 'var(--s-3)' }}>
      <div className="eyebrow">{title}</div>
      {note ? <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 4 }}>{note}</p> : null}
    </div>
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Recruitment"
      title={
        <>
          Welcome someone <em className="serif-italic">new</em>
        </>
      }
      width={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!!docProblem} onClick={() => m.mutate()}>
            Create employee
          </Button>
        </>
      }
    >
      <div className="stack">
        {errors._ ? <Callout tone="bad">{errors._[0]}</Callout> : m.error instanceof ApiError ? <Callout tone="bad">{m.error.message}</Callout> : null}
        <div className="grid-12" style={{ gap: 14 }}>
          {heading('Personal')}
          {input('Given name', 'givenName', 6)}
          {input('Family name', 'familyName', 6)}
          {input('Called', 'preferredName', 4, { hint: 'Shown on kiosks and greetings' })}
          {select('Gender', 'gender', 4, [{ id: 'female', name: 'Female' }, { id: 'male', name: 'Male' }], { blank: false })}
          {input('Born on', 'birthDate', 4, { type: 'date' })}
          {input('Born in', 'birthPlace', 6)}
          {select('Religion', 'religionId', 6, lookups.data?.religions, { hint: 'Decides THR feast' })}
          {select('Marital status', 'maritalStatusId', 6, lookups.data?.maritalStatuses)}
          {select('Education', 'educationLevelId', 6, lookups.data?.educationLevels)}

          {heading('Contact and sign-in')}
          <div className="col-6">
            <Field label="Work email" hint={gxMail ? 'Their Hearth sign-in. Leave it empty to sign in with the new GX Mail address.' : 'Their Hearth sign-in.'} error={err('workEmail')}>
              {(p) => <input {...p} className="input" type="email" value={f.workEmail} onChange={set('workEmail')} />}
            </Field>
          </div>
          {input('Mobile', 'mobilePhone', 6, { placeholder: '+62 812 …', inputMode: 'tel' })}
          <div className="col-6">
            <Switch checked={gxMail} onChange={setGxMail} label="Create a GX Mail address" showLabel />
            <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 6 }}>firstname.lastname@mail.globalxtreme.net, created in GX Mail with the hire.</p>
          </div>
          <div className="col-6">
            {gxMail || f.personalEmail ? (
              <Field label="Private email" hint="Gets the set-up link. Without one, you hand the link over." error={err('personalEmail')}>
                {(p) => <input {...p} className="input" type="email" value={f.personalEmail} onChange={set('personalEmail')} />}
              </Field>
            ) : null}
          </div>

          {heading('Placement')}
          <div className="col-6">
            <Field label="Office">
              {(p) => (
                <select {...p} className="select" value={f.officeId} onChange={(e) => setF((x) => ({ ...x, officeId: e.target.value, departmentId: '', divisionId: '', sectionId: '' }))}>
                  {lookups.data?.offices.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {select('Company', 'companyId', 6, lookups.data?.companies, { blank: false })}
          <PlacementFields officeId={f.officeId} value={f} positions={lookups.data?.jobPositions ?? []} errors={{ divisionId: err('divisionId'), sectionId: err('sectionId'), jobPositionId: err('jobPositionId') }} onChange={(patch) => setF((x) => ({ ...x, ...patch }))} />
          {select('Level', 'jobLevelId', 6, lookups.data?.jobLevels)}
          {select('Employment', 'type', 4, [{ id: 'contract', name: 'Contract' }, { id: 'permanent', name: 'Permanent' }, { id: 'probation', name: 'Probation' }, { id: 'internship', name: 'Internship' }, { id: 'freelance', name: 'Freelance' }], { blank: false })}
          {input('Starts', 'startedOn', 4, { type: 'date' })}
          {input('Contract ends', 'contractEndsOn', 4, { type: 'date' })}
          {input('Basic salary', 'basicSalary', 6, { mono: true, inputMode: 'numeric', hint: 'Monthly, in rupiah', placeholder: '5000000', digits: true })}

          {heading('Identity', 'Encrypted when saved; the profile shows only the last digits.')}
          {input('NIK', 'nationalId', 6, { mono: true, inputMode: 'numeric', maxLength: 16, hint: '16 digits', digits: true })}
          {input('Family card (KK)', 'familyCard', 6, { mono: true, inputMode: 'numeric', maxLength: 16, hint: '16 digits', digits: true })}
          {input('NPWP', 'npwp', 6, { mono: true })}
          {select('Tax status (PTKP)', 'ptkpCode', 6, PTKP.map((c) => ({ id: c, name: c })), { blank: false })}
          {input('BPJS Ketenagakerjaan', 'bpjsEmployment', 6, { mono: true })}
          {input('BPJS Kesehatan', 'bpjsHealth', 6, { mono: true })}

          {heading('Salary account', 'Optional now, but payroll can’t pay them without one.')}
          {select('Bank', 'bankId', 6, lookups.data?.banks, { blank: false })}
          {input('Account number', 'accountNo', 6, { mono: true, inputMode: 'numeric', hint: 'BCA 10 digits · Mandiri 13' })}
          {input('Account holder', 'holderName', 6, { placeholder: `${f.givenName} ${f.familyName}`.trim() || 'As on the bank book' })}
          {input('Branch', 'branch', 6)}

          {canUpload ? (
            <>
              {heading('Documents', `Scans or photos: ${DOCUMENT_HINT}. Uploaded right after the hire is created.`)}
              {PERSON_DOCUMENTS.map((d) => (
                <div key={d.kind} className="col-6">
                  <Field label={d.label} error={documentProblem(docs[d.kind] ?? null)} hint={d.kind === 'bank_book' && !f.accountNo ? 'Needs the account number above' : undefined}>
                    {(p) => <input {...p} className="input" type="file" accept={DOCUMENT_TYPES} disabled={d.kind === 'bank_book' && !f.accountNo} onChange={(e) => setDocs((x) => ({ ...x, [d.kind]: e.target.files?.[0] ?? undefined }))} />}
                  </Field>
                </div>
              ))}
            </>
          ) : null}
        </div>
        <Callout tone="info">No default password is set. The new hire gets a one-time link to choose their own.</Callout>
      </div>
      <SetupLinkDialog
        link={handOver?.link ?? null}
        name={f.givenName}
        onClose={() => {
          const id = handOver!.id;
          setHandOver(null);
          done(id);
        }}
      />
    </Dialog>
  );
}
